/*
 *  -----------------------------------------------------------------------
 *    <copyright file="observations-grid-v2.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { orderBy } from '@progress/kendo-data-query';
import cn from 'clsx';
import { ICMObservationAndActionExtended } from '../../pages/central-monitoring/manage-observations.interface';
import { GridColumnDateFilterV2, GridColumnMenuActiveFilterV2 } from '../core/grid-filters-v2';
import ControlAction from './control-action';
import { ObservationsGridV2Props } from './observations-grid-v2.interface';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useTranslation, useGridCells, useBoolean } from 'hooks';
import { handleTimeInDates, reorderArray } from 'utils/filter';
import ChildActionsGridV2 from 'views/components/central-monitoring/child-actions-grid-v2';
import ExpandableGridV2 from 'views/components/core/expandable/grid-v2';

const ObservationsGridV2 = ({
    title,
    observations,
    rowActions,
    study,
    hideCheckbox = false,
    detailClass,
    canEdit,
    isCMR,
    type,
    className,
    extraMenuItems,
    onClose,
    withinSplitter,
}: ObservationsGridV2Props) => {
    const { t } = useTranslation();
    const { DateCell, MaxLengthCell, NumberCell, YesNoCell, ActiveInactiveCell } = useGridCells();

    const [data, setData] = useState([]);

    const showDeletedItems = useBoolean(false);

    useEffect(() => {
        let filteredData = [...observations].map((item: any) => ({
            ...item,
            _isClosedDateEmpty: Boolean(item.closedDate),
        }));
        // we need to have this logic when we have two fields for sorting which are dates or when the first sorting field is date
        let arrayToSort = filteredData.slice();
        arrayToSort = handleTimeInDates(arrayToSort, ['updated']);
        arrayToSort =
            orderBy(arrayToSort, [
                { field: '_isClosedDateEmpty', dir: 'asc' },
                { field: 'updated', dir: 'desc' },
            ]) ?? [];
        filteredData = reorderArray(filteredData, arrayToSort);
        // end of logic
        const newData = restructuredUpdatedCreatedBy(filteredData);
        setData(newData);
    }, [observations]);

    // Used by max length cells to prevent clipping out of the page
    const tooltipHeight = '30vh';

    return (
        <div className={cn({ 'child-details-grid-wrapper': withinSplitter })}>
            <ExpandableGridV2
                title={title}
                customKey="manage-observations-grid"
                className={cn(className, 'child-details-grid')}
                id="observationsGrid"
                key="observationsGrid"
                items={data}
                rowActions={rowActions}
                groupable
                showPager
                hideCheckbox={hideCheckbox}
                scrollable="scrollable"
                reorderable
                pageable={false}
                resizable
                detailClass={detailClass}
                showDeletedItems={showDeletedItems}
                detail={(observation: ICMObservationAndActionExtended) => {
                    if (
                        observation?.actions?.length &&
                        (showDeletedItems.value || observation.actions.find((item) => !item.deleted))
                    ) {
                        return (
                            <ControlAction studyId={study.id}>
                                {(handleDeleteAct: Function, handleRestoreAct: Function) => (
                                    <ChildActionsGridV2
                                        title={t('ra:actions')}
                                        study={study}
                                        observation={observation}
                                        key="child-action-grid"
                                        canEdit={canEdit}
                                        isCMR={isCMR}
                                        type={type}
                                        showDeletedItems={showDeletedItems}
                                        handleDeleteAct={handleDeleteAct}
                                        handleRestoreAct={handleRestoreAct}
                                        onClose={onClose}
                                    />
                                )}
                            </ControlAction>
                        );
                    }
                    return null;
                }}
                columns={[
                    {
                        field: 'deleted',
                        id: 'observation-associated-deleted',
                        label: t('deleted'),
                        minWidth: 136,
                        showByDefault: false,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilterV2,
                    },
                    {
                        label: t('ra:actions'),
                        id: 'observation-associated-actions',
                        field: '_numberOfActions',
                        lockedByDefault: true,
                        filter: 'numeric',
                        minWidth: 132,
                        cells: { data: NumberCell },
                    },
                    {
                        label: t('ra:observation'),
                        id: 'observation-associated-observation',
                        field: 'description',
                        lockedByDefault: true,
                        minWidth: 174,
                        cells: { data: MaxLengthCell(50, tooltipHeight) },
                    },
                    {
                        field: '_site',
                        id: 'observation-associated-site',
                        label: t('site'),
                        minWidth: 108,
                    },
                    {
                        field: 'observationDate',
                        id: 'observation-associated-observationDate',
                        label: t('ra:observationDate'),
                        filter: 'date',
                        minWidth: 164,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        field: 'potentialCause',
                        id: 'observation-associated-potentialCause',
                        label: t('ra:potentialCause'),
                        minWidth: 174,
                        cells: { data: MaxLengthCell(50, tooltipHeight) },
                    },
                    {
                        field: 'confirmedCause',
                        id: 'observation-associated-confirmedCause',
                        label: t('ra:confirmedCause'),
                        minWidth: 174,
                        cells: { data: MaxLengthCell(50, tooltipHeight) },
                    },
                    {
                        field: 'comments',
                        id: 'observation-associated-comments',
                        label: t('ra:comments'),
                        minWidth: 174,
                        cells: { data: MaxLengthCell(50, tooltipHeight) },
                    },
                    {
                        field: '_ownerLabel',
                        id: 'observation-associated-owner',
                        label: t('ra:owner'),
                        minWidth: 126,
                        cells: {
                            data: ActiveInactiveCell(
                                (dataItem: any) => dataItem._owner?.active === false,
                                (dataItem: any) => dataItem._ownerLabel,
                            ),
                        },
                    },
                    {
                        field: 'closedDate',
                        id: 'observation-associated-closedDate',
                        label: t('ra:closedDate'),
                        filter: 'date',
                        minWidth: 128,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        field: '_recentActionDate',
                        id: 'observation-associated-recentActionDate',
                        label: t('ra:recentAction'),
                        filter: 'date',
                        minWidth: 132,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        field: '_openAction',
                        id: 'observation-associated-openAction',
                        label: t('ra:openActions'),
                        filter: 'numeric',
                        minWidth: 132,
                    },
                    {
                        field: '_datasetDate',
                        id: 'observation-associated-dataset-date',
                        label: t('cm:datasetDate'),
                        filter: 'date',
                        minWidth: 136,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        field: '_panelName',
                        id: 'observation-associated-panel-name',
                        label: t('cm:panelName'),
                        minWidth: 124,
                    },
                    {
                        label: t('ra:updatedDate'),
                        id: 'observation-associated-updatedDate',
                        field: 'updated',
                        filter: 'date',
                        minWidth: 142,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        field: '_updatedByString',
                        id: 'observation-associated-updatedByString',
                        label: t('ra:updatedBy'),
                        minWidth: 138,
                    },
                    {
                        field: '_deleteReason',
                        id: 'observation-associated-delete-reason',
                        label: t('cm:deleteReasonLabel'),
                        showByDefault: false,
                        minWidth: 174,
                        cells: { data: MaxLengthCell(50, tooltipHeight) },
                    },
                    {
                        field: '_deleteDate',
                        id: 'observation-associated-delete-date',
                        label: t('cm:deleteDate'),
                        showByDefault: false,
                        filter: 'date',
                        minWidth: 138,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        field: '_deletedBy',
                        id: 'observation-associated-deleted-by',
                        label: t('cm:deletedBy'),
                        showByDefault: false,
                        minWidth: 138,
                    },
                    {
                        field: 'created',
                        id: 'observation-associated-created',
                        label: t('ra:createdDate'),
                        showByDefault: false,
                        filter: 'date',
                        minWidth: 136,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        field: '_createdByString',
                        id: 'observation-associated-createdBy',
                        label: t('ra:createdBy'),
                        minWidth: 138,
                        showByDefault: false,
                    },
                    {
                        field: 'recordId',
                        id: 'observation-associated-recordId',
                        label: 'ID',
                        minWidth: 100,
                        filter: 'numeric',
                        showByDefault: false,
                        cells: { data: NumberCell },
                    },
                    {
                        field: 'closedDatePopulated',
                        id: 'observation-associated-closedDatePopulated',
                        label: t('cm:closedDatePopulatedOnDate'),
                        minWidth: 210,
                        filter: 'date',
                        showByDefault: false,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                ]}
                extraMenuItems={extraMenuItems}
            />
        </div>
    );
};

export default React.memo(ObservationsGridV2);
